import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { PortalShell } from "@/pages/ClientePortal";
import { ArrowLeft, Car, CheckCircle2, AlertTriangle, ClipboardList, DollarSign, Cog, Wrench } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  entrou: "Veículo recebido",
  em_inspecao: "Em inspeção",
  aguardando_estimate: "Preparando orçamento",
  aguardando_aprovacao: "Aguardando sua aprovação",
  aguardando_pecas: "Aguardando peças",
  em_andamento: "Em execução",
  pronto: "Pronto para retirada",
  entregue: "Entregue",
};

const STAGES = [
  { id: "entrou", label: "Recebido" },
  { id: "em_inspecao", label: "Inspeção" },
  { id: "aguardando_estimate", label: "Orçamento" },
  { id: "aguardando_aprovacao", label: "Aprovação" },
  { id: "aguardando_pecas", label: "Peças" },
  { id: "em_andamento", label: "Execução" },
  { id: "pronto", label: "Pronto" },
  { id: "entregue", label: "Entregue" },
];

export default function ClientePortalJob() {
  const { token, jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [approving, setApproving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/${token}/job/${jobId}`);
      setData(data);
    } catch { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token, jobId]);

  const approve = async () => {
    if (!window.confirm("Confirmar aprovação deste serviço? A oficina será notificada e iniciará os trabalhos.")) return;
    setApproving(true);
    try {
      await axios.post(`${API}/portal/${token}/job/${jobId}/approve`);
      toast.success("Serviço aprovado! Obrigado.");
      await load();
    } catch { toast.error("Erro ao aprovar"); }
    finally { setApproving(false); }
  };

  if (loading) return <PortalShell><div className="text-sm text-muted-foreground">Carregando…</div></PortalShell>;
  if (!data) return null;
  const { job, vehicle, inspection, brand, estimate } = data;
  const currentStageIdx = STAGES.findIndex((s) => s.id === job.status);
  const urgent = inspection?.items?.filter((x) => x.status === "urgente") || [];
  const attention = inspection?.items?.filter((x) => x.status === "atencao") || [];

  return (
    <PortalShell brand={brand}>
      <div className="space-y-6" data-testid="portal-job-page">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/portal/${token}`)} className="-ml-2"><ArrowLeft className="w-4 h-4" /> Voltar</Button>

        <div className="border border-border rounded-md bg-card overflow-hidden">
          <div className="h-40 bg-secondary">
            {vehicle?.photo_url ? <img src={vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
              <div className="w-full h-full flex items-center justify-center"><Car className="w-10 h-10 text-muted-foreground" /></div>}
          </div>
          <div className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Serviço</div>
            <div className="font-display text-2xl font-bold tracking-tight">{job.service_type}</div>
            <div className="text-sm text-muted-foreground mt-1">{vehicle?.year} {vehicle?.make} {vehicle?.model} · {vehicle?.plate}</div>

            {/* Progress stages */}
            <div className="mt-6 space-y-2">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
                <span>Progresso</span>
                <span>{job.progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${job.progress}%` }} />
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-1 mt-3">
                {STAGES.map((s, i) => (
                  <div key={s.id} className={`text-[9px] uppercase tracking-wider text-center py-1 rounded ${i <= currentStageIdx ? "bg-primary/15 text-primary font-medium" : "bg-secondary/50 text-muted-foreground"}`}>
                    {s.label}
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <span className={`inline-block text-xs uppercase tracking-wider px-2 py-1 rounded ${job.status === "aguardando_aprovacao" ? "bg-orange-500/15 text-orange-500" : "bg-secondary text-secondary-foreground"}`}>
                  {STATUS_LABEL[job.status]}
                </span>
              </div>

              {job.client_note && (
                <div className="mt-4 border-l-2 border-primary/60 bg-primary/5 rounded-r-md px-4 py-3" data-testid="portal-client-note">
                  <div className="text-[10px] uppercase tracking-widest text-primary mb-1 font-semibold">
                    Mensagem da oficina
                  </div>
                  <div className="text-sm whitespace-pre-line text-foreground">
                    {job.client_note}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {job.status === "aguardando_aprovacao" && (
          <div className="border border-orange-500/40 bg-orange-500/10 rounded-md p-5" data-testid="approve-block">
            <div className="text-[10px] uppercase tracking-widest text-orange-500">Aprovação necessária</div>
            <div className="font-display text-lg font-bold mt-1">Autorizar a execução deste serviço</div>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Ao autorizar, a oficina será notificada e iniciará a solicitação das peças / execução do reparo.
            </p>
            <Button onClick={approve} disabled={approving} data-testid="approve-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
              <CheckCircle2 className="w-4 h-4" />
              {approving ? "Aprovando…" : "Autorizar serviço"}
            </Button>
          </div>
        )}

        {estimate && estimate.items?.length > 0 && (
          <div className="border border-border rounded-md bg-card p-5" data-testid="portal-estimate">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Orçamento</div>
                  <div className="font-display font-bold">Detalhamento de peças e mão de obra</div>
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${estimate.status === "approved" ? "bg-emerald-500/15 text-emerald-500" : estimate.status === "sent" ? "bg-orange-500/15 text-orange-500" : "bg-secondary text-muted-foreground"}`}>
                {estimate.status === "approved" ? "Aprovado" : estimate.status === "sent" ? "Aguardando aprovação" : "Rascunho"}
              </span>
            </div>

            <div className="border border-border rounded overflow-hidden">
              <div className="divide-y divide-border text-sm">
                {estimate.items.map((it, i) => {
                  const qty = it.kind === "labor" ? (it.hours || 0) : (it.quantity || 1);
                  const lineTotal = (Number(qty) || 0) * (Number(it.unit_price) || 0);
                  return (
                    <div key={i} className="px-3 py-2 flex items-center gap-2">
                      {it.kind === "part" ? <Cog className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <Wrench className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{it.description}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {it.kind === "labor" ? `${qty}h × $${Number(it.unit_price).toFixed(2)}` : `${qty} × $${Number(it.unit_price).toFixed(2)}${it.part_number ? ` · ${it.part_number}` : ""}`}
                        </div>
                      </div>
                      <div className="text-sm font-mono">${lineTotal.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${(estimate.subtotal || 0).toFixed(2)}</span></div>
              {(estimate.tax_amount || 0) > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Imposto ({((estimate.tax_rate || 0) * 100).toFixed(1)}%)</span><span className="font-mono">${estimate.tax_amount.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between border-t border-border pt-1.5 mt-1.5">
                <span className="font-display font-bold">TOTAL</span>
                <span className="font-display font-bold text-lg">${(estimate.total || 0).toFixed(2)}</span>
              </div>
            </div>
            {estimate.notes && <div className="mt-3 text-xs text-muted-foreground border-t border-border pt-2">{estimate.notes}</div>}
          </div>
        )}

        {inspection && (
          <div className="border border-border rounded-md bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="w-4 h-4 text-muted-foreground" />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Inspeção realizada</div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="border border-border rounded-md p-3 text-center">
                <div className="font-display text-xl font-bold text-emerald-500">{inspection.items.filter((x) => x.status === "ok").length}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">OK</div>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <div className="font-display text-xl font-bold text-orange-500">{attention.length}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Atenção</div>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <div className="font-display text-xl font-bold text-red-500">{urgent.length}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Urgente</div>
              </div>
            </div>

            {urgent.length + attention.length > 0 && (
              <div className="space-y-2">
                {urgent.map((it) => (
                  <div key={it.id} className="flex items-start gap-2 border border-red-500/30 bg-red-500/5 rounded p-2.5">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium">{it.category} · {it.name}</div>
                      {it.notes && <div className="text-xs text-muted-foreground mt-0.5">{it.notes}</div>}
                    </div>
                  </div>
                ))}
                {attention.map((it) => (
                  <div key={it.id} className="flex items-start gap-2 border border-orange-500/30 bg-orange-500/5 rounded p-2.5">
                    <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium">{it.category} · {it.name}</div>
                      {it.notes && <div className="text-xs text-muted-foreground mt-0.5">{it.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {inspection.observations && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Observações do técnico</div>
                <div className="text-sm whitespace-pre-line">{inspection.observations}</div>
              </div>
            )}

            {inspection.photos?.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Fotos</div>
                <div className="grid grid-cols-3 gap-2">
                  {inspection.photos.map((p, i) => <img key={i} src={p} alt="" className="w-full h-20 object-cover rounded border border-border" />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PortalShell>
  );
}
